import { io, Socket } from 'socket.io-client'
import { captureException } from '@sentry/react'
import config from '../config'

export enum SocketEvent {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  MESSAGE_CREATED = 'chat:message-created',
  RECEIPT_MOVED = 'chat:receipt-moved',
  ROOM_UPDATED = 'chat:room-updated',
  ROOMS_NEW = 'rooms:new',
  NOTIFICATION_COUNTS = 'notification:counts',
  TICKET_UPDATED = 'ticket:updated',
  FEATURES_UPDATED = 'features:updated',
  SOCIETY_SETTINGS_UPDATED = 'society:settings-updated',
  POST_PUBLISHED = 'post:published',
  POST_UPDATED = 'post:updated',
  COMMENT_UPDATED = 'comment:updated',
  EVENT_UPDATED = 'event:updated',
  BOOKING_UPDATED = 'booking:updated',
  REGISTER_UPDATED = 'register:updated',
  MEMBERSHIP_UPDATED = 'membership:updated',
  BINDER_UPDATED = 'binder:updated',
  CONTENT_UPDATED = 'content:updated',
  USER_UPDATED = 'user:updated',
}

const shouldReportToSentry = (error: Error): boolean => {
  const networkErrorMessages = [
    'Network Error',
    'timeout',
    'socket hang up',
    'Network request failed',
    'websocket error',
  ]

  // Ignore errors if the message indicates a network error that is common and usually transient
  if (
    networkErrorMessages.some((errorMessage) =>
      error.message.includes(errorMessage)
    )
  ) {
    return false
  }
  return true
}

class SocketService {
  protected _socket: Socket | null

  constructor() {
    this._socket = null
  }

  public async setSocket(
    token: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onEventHandlers: { event: SocketEvent; func: (data: any) => void }[]
  ): Promise<void> {
    if (token == null || token.length === 0) {
      return
    }

    this.unsetSocket()

    try {
      const _socket = io(config.BASE_URL, {
        path: '/chat/socket',
        transports: ['websocket'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      })

      this._socket = _socket

      onEventHandlers.forEach((eventHandler) => {
        _socket.on(eventHandler.event, eventHandler.func)
      })

      _socket.on('connect_error', (error: Error) => {
        if (shouldReportToSentry(error)) {
          captureException(error)
        }
      })

      _socket.on('error', (error: Error) => {
        // Handle generic socket errors
        if (shouldReportToSentry(error)) {
          captureException(error)
        }
      })
    } catch (error) {
      captureException(error)
    }
  }

  /**
   * Tears down the socket for chat comms
   */
  public unsetSocket(): void {
    if (this._socket == null) {
      return
    }
    this._socket.removeAllListeners()
    this._socket.disconnect()
    this._socket = null
  }

  /**
   * gets the socket for chat comms
   */
  public getSocket(): Socket | null {
    return this._socket
  }
}

export const socket = new SocketService()
